/**
 * TAP Application Router
 *
 * This module defines the main routing structure for the TAP application.
 * All routes are protected and require authentication (handled by SecuredApp wrapper).
 *
 * Route Structure:
 * - /home              - Dashboard with licensing jobs overview
 * - /windowSequence    - Window sequence management (blocked for Digital Stores users)
 * - /rateCards         - Rate card creation and management (blocked for Digital Stores users)
 * - /licensingJobs     - Licensing job management (blocked for Digital Stores users)
 * - /offers            - Offer table for a specific licensing job (`?jobId=`, blocked for Digital Stores users)
 * - /offerHistory      - History view for a specific offer (`?jobId=&offerId=`, blocked for Digital Stores users)
 * - /pmxCuratedDatesAndPrices - PMX pricing data (blocked for Digital Stores users)
 * - /pmxDeliveries     - PMX delivery tracking (Job Tracker only, blocked for Digital Stores)
 * - /updateEngine      - Update engine job management (blocked for Digital Stores users)
 * - /tapJobs           - TAP job monitoring (Job Tracker only, blocked for Digital Stores)
 * - /tprExceptions     - TPR exception handling (TAP Admin or Digital Stores only)
 * - /ppt               - Promo Planning Module (TAP Admin or Digital Stores only)
 * - /users             - User administration (admin only)
 */
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { initialize } from "./store/auth";
import { Routes, Route } from "react-router-dom";
import { Header } from "./components/Header";
import { Spinner } from "./components/Spinner";
import { PageNavigation } from "./components/PageNavigation";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { DialogsProvider } from "./components/dialogs";
import { UsersPage } from "./pages/Users";
import { PMXDeliveriesPage } from "./pages/PMXDeliveries";
import { PMXPricesAndDatesPage } from "./pages/PMXPricesAndDates";
import { JobsPage } from "./pages/TAPJobs";
import { UpdateEnginePage } from "./pages/UpdateEngine";
import { HomePage } from "./pages/Home";
import { TPRExceptionsPage } from "./pages/TPRExceptions";
import { RateCardsPage } from "./pages/RateCards";
import { LicensingJobsPage } from "./pages/LicensingJobs";
import { OffersPage } from "./pages/Offers";
import { OfferHistoryPage } from "./pages/OfferHistory";
import { WindowsPage } from "./pages/Windows/index.jsx";
import { PPTHomePage } from "tap/pages/PPT/Home/index.jsx";
import { PPTPromotionPage } from "tap/pages/PPT/Promotion/index.jsx";
import { PPMPreferencesGate } from "tap/pages/PPT/components/PPMPreferencesGate/index.jsx";

/**
 * Main router component for the TAP application.
 * Initializes auth state on mount and renders the route structure.
 *
 * @returns {JSX.Element} The router with all application routes
 */
export default function TapRouter() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(initialize());
  }, [dispatch]);

  return (
    <>
      <Spinner />
      <Header />
      <DialogsProvider>
        <PageNavigation>
          <Routes>
            {/* Home - available to all authenticated users */}
            <Route path="/home" element={<HomePage />} />

            {/* Routes blocked for Digital Stores users */}
            <Route
              path="/updateEngine"
              element={
                <ProtectedRoute blockDigitalStores>
                  <UpdateEnginePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tapJobs"
              element={
                <ProtectedRoute requireJobTracker blockDigitalStores>
                  <JobsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pmxCuratedDatesAndPrices"
              element={
                <ProtectedRoute blockDigitalStores>
                  <PMXPricesAndDatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pmxDeliveries"
              element={
                <ProtectedRoute requireJobTracker blockDigitalStores>
                  <PMXDeliveriesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rateCards"
              element={
                <ProtectedRoute blockDigitalStores>
                  <RateCardsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/licensingJobs"
              element={
                <ProtectedRoute blockDigitalStores>
                  <LicensingJobsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/offers"
              element={
                <ProtectedRoute blockDigitalStores>
                  <OffersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/offerHistory"
              element={
                <ProtectedRoute blockDigitalStores>
                  <OfferHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/windowSequence"
              element={
                <ProtectedRoute blockDigitalStores>
                  <WindowsPage />
                </ProtectedRoute>
              }
            />

            {/* TPR Exceptions - TAP Admin or Digital Stores users only */}
            <Route
              path="/tprExceptions"
              element={
                <ProtectedRoute requireTapAdminOrDigitalStores>
                  <TPRExceptionsPage />
                </ProtectedRoute>
              }
            />

            {/* Promo Planning Module - TAP Admin or Digital Stores users only.
                Pathless layout route so the permission check and the one
                per-session grid-preferences fetch are declared once for every
                PPM page rather than repeated per route. */}
            <Route
              element={
                <ProtectedRoute requireTapAdminOrDigitalStores>
                  <PPMPreferencesGate />
                </ProtectedRoute>
              }
            >
              <Route path="/ppt" element={<PPTHomePage />} />
              <Route
                path="/ppt/promotion/:id"
                element={<PPTPromotionPage />}
              />
            </Route>

            {/* Users - Admin only */}
            <Route
              path="/users"
              element={
                <ProtectedRoute requireAdmin>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </PageNavigation>
      </DialogsProvider>
    </>
  );
}
